<script setup>
import { computed } from 'vue';

const props = defineProps({
  label: {
    type: [Object, String],
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  color: {
    type: String,
    default: 'slate',
    validator: value =>
      ['slate', 'amber', 'teal', 'green', 'ruby', 'blue', 'iris'].includes(
        value
      ),
  },
});

const COLOR_CLASSES = {
  slate: 'bg-n-label-color text-n-slate-12',
  amber: 'bg-n-amber-2 text-n-amber-11',
  teal: 'bg-n-teal-2 text-n-teal-11',
  green: 'bg-[#DBF5E1] text-[#2CA54A] dark:bg-[#2CA54A]/15 dark:text-[#2CA54A]',
  ruby: 'bg-n-ruby-2 text-n-ruby-11',
  blue: 'bg-n-blue-2 text-n-blue-11',
  iris: 'bg-n-iris-2 text-n-iris-11',
};

const isStringLabel = computed(() => typeof props.label === 'string');

const labelTitle = computed(() => {
  return isStringLabel.value ? props.label : props.label?.title;
});

const labelDescription = computed(() => {
  return (!isStringLabel.value && props.label?.description) || '';
});

const labelColor = computed(() => {
  return isStringLabel.value ? null : props.label.color;
});

const colorClasses = computed(() => COLOR_CLASSES[props.color]);
</script>

<template>
  <div
    :title="labelDescription"
    class="inline-flex w-fit flex-shrink-0 items-center rounded-[7px] [color-scheme:normal]"
    :class="[colorClasses, compact ? 'h-6 gap-1 px-1.5' : 'h-8 gap-1.5 px-2.5']"
  >
    <span
      v-if="labelColor"
      class="rounded-lg flex-shrink-0"
      :class="compact ? 'size-1.5' : 'size-2'"
      :style="{ background: labelColor }"
    />
    <slot v-else name="icon" />
    <span
      class="flex min-w-0 items-center gap-1 whitespace-nowrap"
      :class="
        compact ? 'text-xs font-semibold leading-4' : 'text-label !font-420'
      "
    >
      <slot name="label">{{ labelTitle }}</slot>
    </span>
    <slot name="action" />
  </div>
</template>
